import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  initializeFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { encryptClientDetails, usableBookingKey, type EncryptedClientData } from './bookingEncryption';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
};

const app = initializeApp(firebaseConfig);

/** App’s Firestore database id (not the console’s “(default)” unless you opt in). */
const DEFAULT_FIRESTORE_DATABASE_ID = 'bookingsite';

const useLongPolling =
  import.meta.env.VITE_FIRESTORE_EXPERIMENTAL_LONG_POLLING === 'true';

function resolveFirestoreDatabaseId(): string {
  const fromEnv = import.meta.env.VITE_FIRESTORE_DATABASE_ID?.trim();
  if (fromEnv === '(default)') return '(default)';
  if (fromEnv) return fromEnv;
  return DEFAULT_FIRESTORE_DATABASE_ID;
}

const firestoreDatabaseId = resolveFirestoreDatabaseId();

/**
 * Long polling helps Firestore complete on some corporate / proxy networks where
 * WebChannel stalls. Enable with VITE_FIRESTORE_EXPERIMENTAL_LONG_POLLING=true.
 */
function initFirestoreDb() {
  const usePlatformDefaultDb = firestoreDatabaseId === '(default)';

  if (useLongPolling) {
    const settings = { experimentalForceLongPolling: true };
    return usePlatformDefaultDb
      ? initializeFirestore(app, settings)
      : initializeFirestore(app, settings, firestoreDatabaseId);
  }

  return usePlatformDefaultDb ? getFirestore(app) : getFirestore(app, firestoreDatabaseId);
}

export const db = initFirestoreDb();

/** Match deployed callable region (see `functions/src/createBooking.ts`). */
const FUNCTIONS_REGION =
  (import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined)?.trim() || 'us-central1';
export const functions = getFunctions(app, FUNCTIONS_REGION);

/**
 * Local dev only: set VITE_FUNCTIONS_EMULATOR_HOST=127.0.0.1:5001 to send
 * callables to `firebase emulators:start --only functions,firestore`.
 */
const functionsEmulatorHost = import.meta.env.DEV
  ? (import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST as string | undefined)?.trim()
  : undefined;
export const usingFunctionsEmulator = Boolean(functionsEmulatorHost);
if (functionsEmulatorHost) {
  const [host, port] = functionsEmulatorHost.split(':');
  connectFunctionsEmulator(functions, host, Number(port) || 5001);
}

/**
 * Bookingsite DB uses `public_listings` only (see deployed rules).
 * `businesses` is omitted from reads here to avoid permission errors when rules do not define it.
 */
export const LISTING_PARENTS = ['public_listings'] as const;

const APPROVED_BOOKER_SUBCOLS = ['approved_booker_hashes', 'approved_bookers'] as const;

function isFirebaseClientConfigured(): boolean {
  return Boolean(firebaseConfig.projectId?.trim() && firebaseConfig.apiKey?.trim());
}

// ============================================================================
// SUBDOMAIN EXTRACTION
// ============================================================================

/**
 * Extract subdomain from hostname
 * Examples:
 * - josh-massage.yoursite.com -> josh-massage
 * - localhost:3001 -> null (for local development, use query param)
 * - yoursite.com -> null (main domain, no subdomain)
 */
export function extractSubdomain(hostname: string): string | null {
  // For local development, check for ?subdomain= query param
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    const params = new URLSearchParams(window.location.search);
    return params.get('subdomain');
  }

  const parts = hostname.split('.');

  // Need at least subdomain.domain.tld (3 parts)
  if (parts.length < 3) {
    return null;
  }

  const subdomain = parts[0];

  // 'www' is not a business subdomain
  if (subdomain === 'www') {
    return null;
  }

  return subdomain;
}

// ============================================================================
// ENCRYPTION UTILITIES
// ============================================================================

async function generateEncryptionKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    encoder.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Convert a Uint8Array to a base64 string for Firestore string storage.
 */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export async function encryptContactInfo(
  contactData: any,
  businessId: string
): Promise<{ encryptedData: string; iv: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await generateEncryptionKey(businessId, salt);

  const iv = crypto.getRandomValues(new Uint8Array(12)); // 12 bytes for GCM
  const encoder = new TextEncoder();
  const data = encoder.encode(JSON.stringify(contactData));

  const encryptedData = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    data as BufferSource
  );

  // Prepend salt (16 bytes) to encrypted data so it can be used for decryption
  const encryptedBytes = new Uint8Array(encryptedData);
  const combined = new Uint8Array(salt.length + encryptedBytes.length);
  combined.set(salt, 0);
  combined.set(encryptedBytes, salt.length);

  // Return as base64 strings for Firestore string fields
  return {
    encryptedData: toBase64(combined),
    iv: toBase64(iv),
  };
}

// ============================================================================
// SHA-256 HASHING (for approved booker lookup)
// ============================================================================

/**
 * SHA-256 hash a string and return as lowercase hex.
 * Used for matching against approved-booker hash document IDs (e.g. approved_booker_hashes).
 */
export async function hashSHA256(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(input));
  return Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Check if the customer's email or phone hash exists under approved_booker_hashes
 * (or legacy approved_bookers). Point lookups by doc id = hash only.
 */
export async function checkApprovedBooker(
  listingId: string,
  emailHash: string | null,
  phoneHash: string | null
): Promise<boolean> {
  const hashes = [emailHash, phoneHash].filter((h): h is string => !!h);
  if (hashes.length === 0) return false;

  for (const parent of LISTING_PARENTS) {
    for (const subcol of APPROVED_BOOKER_SUBCOLS) {
      try {
        const results = await Promise.all(
          hashes.map(h => getDoc(doc(db, parent, listingId, subcol, h)))
        );
        if (results.some(snap => snap.exists())) return true;
      } catch (err) {
        console.error(`Error checking approved booker (${parent}/${subcol}):`, err);
      }
    }
  }
  return false;
}

// ============================================================================
// PUBLIC DATA QUERIES
// ============================================================================

function normalizeListingDoc(
  id: string,
  data: Record<string, any>
): Record<string, any> {
  return {
    id,
    ...data,
    business_name: data.business_name ?? data.businessName,
    published:
      data.published ??
      data.is_published ??
      data.isPublished ??
      data.active ??
      true,
  };
}

async function getDocsOrNull(
  label: string,
  q: Parameters<typeof getDocs>[0]
): Promise<Awaited<ReturnType<typeof getDocs>> | null> {
  try {
    return await getDocs(q);
  } catch (err) {
    console.error(`Error fetching business listing (${label}):`, err);
    return null;
  }
}

export async function getBusinessListing(subdomain: string) {
  if (!isFirebaseClientConfigured()) {
    console.error(
      'Firestore is not configured: set VITE_FIREBASE_PROJECT_ID and VITE_FIREBASE_API_KEY (and related VITE_FIREBASE_* vars).'
    );
    return null;
  }

  // Must align with isPublishedListing() in bookingsite rules (published / is_published).
  const qPublic = query(
    collection(db, 'public_listings'),
    where('subdomain', '==', subdomain),
    where('published', '==', true),
    limit(1)
  );
  const qPublicAlt = query(
    collection(db, 'public_listings'),
    where('subdomain', '==', subdomain),
    where('is_published', '==', true),
    limit(1)
  );

  const [publicSnap, publicAltSnap] = await Promise.all([
    getDocsOrNull('public_listings', qPublic),
    getDocsOrNull('public_listings (is_published)', qPublicAlt),
  ]);

  const pick = (snap: Awaited<ReturnType<typeof getDocs>> | null) => {
    if (snap && !snap.empty) {
      const d = snap.docs[0];
      return normalizeListingDoc(d.id, d.data() as Record<string, any>) as any;
    }
    return null;
  };

  return pick(publicSnap) ?? pick(publicAltSnap);
}

export async function getBusinessListingByDomain(domain: string) {
  const qPublic = query(
    collection(db, 'public_listings'),
    where('custom_domain', '==', domain),
    where('published', '==', true),
    limit(1)
  );
  const qPublicAlt = query(
    collection(db, 'public_listings'),
    where('custom_domain', '==', domain),
    where('is_published', '==', true),
    limit(1)
  );

  const [publicSnap, publicAltSnap] = await Promise.all([
    getDocsOrNull('public_listings by domain', qPublic),
    getDocsOrNull('public_listings by domain (is_published)', qPublicAlt),
  ]);

  const pick = (snap: Awaited<ReturnType<typeof getDocs>> | null) => {
    if (snap && !snap.empty) {
      const d = snap.docs[0];
      return normalizeListingDoc(d.id, d.data() as Record<string, any>) as any;
    }
    return null;
  };

  return pick(publicSnap) ?? pick(publicAltSnap);
}

export async function getListingImages(listingId: string): Promise<string[]> {
  for (const parent of LISTING_PARENTS) {
    try {
      const snap = await getDocs(
        query(
          collection(db, parent, listingId, 'gallery'),
          orderBy('sort_order')
        )
      );
      if (!snap.empty) {
        return snap.docs.map(d => d.data().public_url as string);
      }

      const listingSnap = await getDoc(doc(db, parent, listingId));
      if (listingSnap.exists()) {
        const data = listingSnap.data();
        const urls = (data.cover_image_urls ?? data.cover_images ?? []) as string[];
        if (urls.length > 0) return urls;
      }
    } catch (err) {
      console.error(`Error fetching listing images (${parent}):`, err);
    }
  }
  return [];
}

/**
 * Public service catalog: one document `services/catalog` with `{ services: Service[], updated_at }`.
 * Each service includes explicit `id` (public service id for booking requests).
 */
export async function getPublicServices(listingId: string) {
  for (const parent of LISTING_PARENTS) {
    try {
      const snap = await getDoc(doc(db, parent, listingId, 'services', 'catalog'));
      if (!snap.exists()) continue;
      const data = snap.data() as Record<string, unknown>;
      const raw = data.services;
      if (!Array.isArray(raw)) return [];

      const rows = raw
        .map((item, index) => {
          if (!item || typeof item !== 'object') return null;
          const o = item as Record<string, unknown>;
          const id = typeof o.id === 'string' && o.id.trim() ? o.id.trim() : null;
          if (!id) {
            console.warn(`Service catalog entry missing id (index ${index}); skipped`);
            return null;
          }
          return { ...o, id } as Record<string, unknown>;
        })
        .filter((row): row is Record<string, unknown> => row !== null)
        .filter(
          row => row.visible === true && row.bookable_online === true
        ) as any[];

      rows.sort(
        (a, b) => (Number(a.sort_order) ?? 0) - (Number(b.sort_order) ?? 0)
      );
      return rows;
    } catch (err) {
      console.error(`Error fetching services catalog (${parent}):`, err);
    }
  }
  return [];
}

/**
 * Published reviews: one document `reviews/published` with `{ reviews: Review[], updated_at }`.
 * Each review is `{ rating, review_text, reviewer_name }` only — stable `id` is synthetic for React keys.
 */
export async function getPublicReviews(listingId: string) {
  for (const parent of LISTING_PARENTS) {
    try {
      const snap = await getDoc(doc(db, parent, listingId, 'reviews', 'published'));
      if (!snap.exists()) continue;
      const data = snap.data() as Record<string, unknown>;
      const raw = data.reviews;
      if (!Array.isArray(raw)) return [];

      return raw
        .map((item, index) => {
          if (!item || typeof item !== 'object') return null;
          const o = item as Record<string, unknown>;
          const rating = Number(o.rating);
          const reviewer_name =
            typeof o.reviewer_name === 'string' ? o.reviewer_name : '';
          const review_text =
            typeof o.review_text === 'string' ? o.review_text : undefined;
          if (!reviewer_name || Number.isNaN(rating)) return null;
          return {
            id: `published-${index}`,
            rating,
            reviewer_name,
            ...(review_text !== undefined ? { review_text } : {}),
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null) as any[];
    } catch (err) {
      console.error(`Error fetching published reviews (${parent}):`, err);
    }
  }
  return [];
}

function sortLocations<T extends { sort_order?: number }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => (Number(a.sort_order) ?? 0) - (Number(b.sort_order) ?? 0));
}

export async function getPublicLocations(listingId: string) {
  for (const parent of LISTING_PARENTS) {
    try {
      const snap = await getDocs(
        query(collection(db, parent, listingId, 'locations'), orderBy('sort_order'))
      );
      if (!snap.empty) {
        return snap.docs.map(d => ({ id: d.id, ...d.data() })) as any[];
      }
    } catch (err) {
      try {
        const snap = await getDocs(collection(db, parent, listingId, 'locations'));
        if (!snap.empty) {
          return sortLocations(snap.docs.map(d => ({ id: d.id, ...d.data() })) as any[]);
        }
      } catch (err2) {
        console.error(`Error fetching locations (${parent}):`, err2);
      }
      console.error(`Error fetching locations (${parent}):`, err);
    }
  }
  return [];
}

// ============================================================================
// BETA SIGNUPS
// ============================================================================

export async function insertBetaSignup(email: string) {
  try {
    await addDoc(collection(db, 'beta_signups'), {
      email,
      created_at: serverTimestamp(),
    });
    return { success: true };
  } catch (err: any) {
    return {
      success: false,
      error: err?.message || 'Failed to submit beta signup',
      code: err?.code || null,
    };
  }
}

// ============================================================================
// BOOKING REQUEST CREATION (via Cloud Function for atomic slot allocation)
// ============================================================================

/** The older format. Its key comes from the listing id, so the Firestore rules are its real protection. */
function encryptWithListingId(
  listingId: string,
  customerInfo: unknown,
): Promise<{ encryptedData: string; iv: string }> {
  // In a Web Worker, which keeps PBKDF2 off the main thread
  const worker = new Worker(
    new URL('../workers/crypto.worker.ts', import.meta.url),
    { type: 'module' }
  );

  return new Promise((resolve, reject) => {
    worker.onmessage = (e) => {
      worker.terminate();
      resolve(e.data);
    };
    worker.onerror = (e) => {
      worker.terminate();
      reject(new Error(e.message));
    };
    worker.postMessage({ contactData: customerInfo, businessId: listingId });
  });
}

/**
 * Encrypts to the owner's key once their app has said it can read the result. Anything
 * short of that uses the older format, because a client's booking must never be blocked.
 */
async function encryptCustomerInfo(
  listingId: string,
  customerInfo: unknown,
): Promise<{ clientData: EncryptedClientData } | { encryptedData: string; iv: string }> {
  try {
    const snap = await getDoc(doc(db, 'public_listings', listingId, 'publicProfile', 'bookings'));
    const publicKey = usableBookingKey(snap.data());
    if (publicKey) return { clientData: encryptClientDetails(customerInfo, publicKey) };
  } catch (err) {
    console.warn('Could not encrypt to the owner\'s key, using the older format:', err);
  }
  return encryptWithListingId(listingId, customerInfo);
}

export async function createBookingRequest(requestData: {
  listingId: string;
  serviceId: string;
  preferredDatetime: string;
  serviceDuration: number;
  customerInfo: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    countryCode?: string;
    notes?: string;
  };
  /** For the approved-booker check; the function decides the booking's status. */
  emailHash?: string | null;
  phoneHash?: string | null;
  recaptchaToken: string;
}): Promise<{ success: boolean; status?: 'pending' | 'confirmed'; error?: string }> {
  const clientPayload = await encryptCustomerInfo(requestData.listingId, requestData.customerInfo);

  const startTime = new Date(requestData.preferredDatetime);
  const endTime = new Date(startTime.getTime() + requestData.serviceDuration * 60 * 1000);

  try {
    const callCreateBooking = httpsCallable(functions, 'createBooking');
    const result = await callCreateBooking({
      listingId: requestData.listingId,
      serviceId: requestData.serviceId,
      ...clientPayload,
      requestedStartTime: startTime.toISOString(),
      requestedEndTime: endTime.toISOString(),
      requestedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      emailHash: requestData.emailHash ?? null,
      phoneHash: requestData.phoneHash ?? null,
      recaptchaToken: requestData.recaptchaToken,
    });
    const { status } = (result.data ?? {}) as { status?: 'pending' | 'confirmed' };
    return { success: true, status };
  } catch (err: any) {
    // Re-throw SLOT_UNAVAILABLE so the UI can handle it distinctly
    if (err?.code === 'functions/already-exists' || err?.message?.includes('SLOT_UNAVAILABLE')) {
      throw new Error('SLOT_UNAVAILABLE');
    }
    console.error('Error creating booking request:', err);
    const errorMessage =
      (typeof err?.details === 'string' && err.details) ||
      (typeof err?.message === 'string' && err.message) ||
      (typeof err?.code === 'string' && err.code) ||
      'Failed to create booking request';
    return { success: false, error: errorMessage };
  }
}
